/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useSafeContext, useTheme, supportsHas } from '../utils/index.js';
/**
 * Helper function that returns one of the preset surface elevation values.
 */
const getSurfaceElevationValue = (elevation) => {
    switch (elevation) {
        case 0:
            return 'none';
        case 1:
            return 'var(--iui-shadow-1)';
        case 2:
            return 'var(--iui-shadow-2)';
        case 3:
            return 'var(--iui-shadow-3)';
        case 4:
            return 'var(--iui-shadow-4)';
        case 5:
            return 'var(--iui-shadow-5)';
        default:
            return '';
    }
};
const SurfaceHeader = React.forwardRef((props, ref) => {
    const { as: Element = 'div', children, className, ...rest } = props;
    const { setHasLayout } = useSafeContext(SurfaceContext);
    React.useEffect(() => {
        if (!supportsHas()) {
            setHasLayout(true);
        }
    }, [setHasLayout]);
    return (React.createElement(Element, { className: cx('iui-surface-header', className), ref: ref, ...rest }, children));
});
const SurfaceBody = React.forwardRef((props, ref) => {
    const { as: Element = 'div', children, className, isPadded, ...rest } = props;
    const { setHasLayout } = useSafeContext(SurfaceContext);
    React.useEffect(() => {
        if (!supportsHas()) {
            setHasLayout(true);
        }
    }, [setHasLayout]);
    return (React.createElement(Element, { className: cx('iui-surface-body', className), ref: ref, "data-iui-padded": isPadded ? 'true' : undefined, ...rest }, children));
});
/**
 * The Surface container allows content to appear elevated through the use of a drop shadow
 * @example
 * <Surface>Surface Content</Surface>
 * <Surface elevation={2}>Surface Content</Surface>
 * <Surface>
 *   <Surface.Header>Surface Header Content</Surface.Header>
 *   <Surface.Body isPadded={true}>Surface Body Content</Surface.Body>
 * </Surface>
 */
export const Surface = Object.assign(React.forwardRef((props, ref) => {
    const { elevation, className, style, children, ...rest } = props;
    useTheme();
    const [hasLayout, setHasLayout] = React.useState(false);
    const _style = {
        '--iui-surface-elevation': getSurfaceElevationValue(elevation),
        ...style,
    };
    return (React.createElement("div", { className: cx('iui-surface', className), style: _style, ref: ref, "data-iui-layout": hasLayout ? 'true' : undefined, ...rest },
        React.createElement(SurfaceContext.Provider, { value: { setHasLayout } }, children)));
}), {
    /**
     * 	Surface header subcomponent
     */
    Header: SurfaceHeader,
    /**
     * 	Surface body subcomponent. Additional padding can be added to the body through the 'isPadded' prop
     */
    Body: SurfaceBody,
});
const SurfaceContext = React.createContext(undefined);
export default Surface;
